
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Award, Gift, ArrowRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface PointRedemptionContentProps {
  userId: string;
  availablePoints: number;
}

const PointRedemptionContent: React.FC<PointRedemptionContentProps> = ({ 
  userId, 
  availablePoints 
}) => {
  const { toast } = useToast();
  const [selectedReward, setSelectedReward] = useState<string | null>(null);
  
  const rewards = [
    { id: 'reward1', name: 'Amazon Gift Card', points: 100, description: '$10 Amazon Gift Card' },
    { id: 'reward2', name: 'Starbucks Gift Card', points: 200, description: '$20 Starbucks Gift Card' },
    { id: 'reward3', name: 'Target Gift Card', points: 300, description: '$30 Target Gift Card' },
    { id: 'reward4', name: 'Cash Payout', points: 500, description: '$50 Cash Payout' },
  ];

  const handleRedeemPoints = () => {
    if (!selectedReward) return;
    
    const reward = rewards.find(r => r.id === selectedReward);
    
    if (!reward) return;
    
    toast({
      title: "Points Redeemed!",
      description: `You redeemed ${reward.points} points for a ${reward.name}`,
    });
    
    setSelectedReward(null);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center text-xl">
            <Award className="mr-2 h-5 w-5 text-amber-500" />
            Your Available Points
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-baseline">
            <span className="text-4xl font-bold text-primary">{availablePoints}</span>
            <span className="ml-2 text-gray-500">points available</span>
          </div>
        </CardContent>
      </Card>

      <h2 className="text-xl font-semibold mt-8 mb-4">Available Rewards</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rewards.map((reward) => (
          <Card 
            key={reward.id}
            className={`cursor-pointer transition-all ${
              selectedReward === reward.id 
                ? 'border-2 border-primary shadow-md' 
                : 'hover:shadow-md'
            }`}
            onClick={() => setSelectedReward(reward.id)}
          >
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-medium text-lg">{reward.name}</h3>
                  <p className="text-gray-500 text-sm">{reward.description}</p>
                </div>
                <div className="bg-primary/10 text-primary font-medium px-3 py-1 rounded-full">
                  {reward.points} points
                </div>
              </div>
              
              <div className="mt-4">
                <Button
                  variant={selectedReward === reward.id ? "default" : "outline"}
                  className="w-full"
                  disabled={availablePoints < reward.points}
                  onClick={() => setSelectedReward(reward.id)}
                >
                  {availablePoints < reward.points ? (
                    "Not enough points"
                  ) : selectedReward === reward.id ? (
                    "Selected"
                  ) : (
                    "Select"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      <div className="flex justify-end mt-8">
        <Button
          onClick={handleRedeemPoints}
          disabled={!selectedReward || availablePoints < (rewards.find(r => r.id === selectedReward)?.points || 0)}
          size="lg"
        >
          Redeem Points
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default PointRedemptionContent;
